<script lang="ts" setup>
import { ref, useAsyncData } from '#imports';

const { QueryWTFApiKey, search } = useWTFApi();

defineProps<{
    isMobile?: boolean;
}>();

const searchValue = ref('');
const isOpen = ref(false);

const {
    data: collectionList,
    status: collectionListStatus
} = useAsyncData(QueryWTFApiKey.SEARCH_COLLECTIONS, async () => {
    if (searchValue.value && searchValue.value.length >= 1) {
        return await search.getAutocompleteCollections({ pattern: searchValue.value });
    }
    return { data: [] };
}, {
    transform: ({ data }) => {
        return data.map(({ imageUrl, items, name, slug }) => ({
            imageUrl,
            amount: items,
            name,
            link: `/collections/${slug}`
        })) ?? null;
    },
    watch: [searchValue]
});

const {
    data: nftList,
    status: nftListStatus
} = useAsyncData(QueryWTFApiKey.SEARCH_NFTS, async () => {
    if (searchValue.value && searchValue.value.length >= 1) {
        return await search.getAutocompleteNfts({ pattern: searchValue.value });
    }
    return { data: [] };
}, {
    transform: ({ data }) => {
        return data.map((item) => {
            const { collectionSlug, displayImageURL, name, identifier } = item;
            return {
                imageUrl: displayImageURL,
                name,
                link: `/collections/${collectionSlug}/${identifier}`
            };
        });
    },
    watch: [searchValue]
});

</script>

<template>
  <Dropdown dropdown-content-class="header-search-content"
            dropdown-class="width-full"
            :position="isMobile ? 'headerFixedAll' : 'bottomRight'"
            :is-open="isOpen"
            @update:is-open="(value:boolean) => isOpen = value"
  >
    <template #toggle>
      <SearchInput aria-label="search"
                   @on-change-debounce="(val:string) => searchValue = val"
                   @on-focus="isOpen = true"
      />
    </template>
    <template #content>
      <div class="header-search-container border-500">

        <div v-if="isMobile"
             class="display-flex flex-row align-items-center header-search-input header-search--mobile"
        >
          <SearchInput container-class="header-search-input__elem"
                       :without-remove-icon="true"
                       aria-label="search"
                       :is-focused="isOpen && isMobile"
                       @on-change-debounce="(val:string) => searchValue = val"
                       @on-focus="isOpen = true"
          />
          <div class="header-search-input__close display-flex align-items-center justify-center cursor-pointer"
               type="button"
               role="button"
               aria-label="close"
               @click="isOpen = false"
          >
            <IconMain :size="24"
                      icon="close"
            />
          </div>
        </div>
        <div class="header-search-container__list">
          <template v-if="collectionList || nftList">
            <HeaderSearchContent v-if="collectionList"
                                 :data="collectionList"
                                 title="Popular collections"
                                 class="header-search-collection"
                                 :is-loading="collectionListStatus === 'pending'"
            />
            <HeaderSearchContent v-if="nftList"
                                 :data="nftList"
                                 title="Assets"
                                 :is-loading="nftListStatus === 'pending'"
            />
          </template>
          <div v-else
               class="header-search-missing"
          >
            <p class="text-400-default">
              Recent searches
            </p>
            <p class="display-500-accent header-search-missing__text">
              No recent searches
            </p>
          </div>
        </div>

      </div>
    </template>
  </Dropdown>
</template>

<style lang="scss" scoped>
.header-search {
  &-input {
    flex-wrap: nowrap;
    margin-right: $spacing-400;
    margin-bottom: $spacing-400;
    margin-left: $spacing-400;

    &__elem {
      flex-grow: 1;
    }

    &__close {
      align-self: stretch;
      padding: 0 16px;
    }

  }

  &--mobile {
    display: none;

    @include tablet-768 {
      display: flex;;
    }
  }

  &-container {
    display: flex;
    flex-direction: column;
    max-height: 100%;
    padding-top: $spacing-400;
    padding-bottom: $spacing-400;
    background-color: var(--layer-floor-2);

    &__list {
      overflow-y: auto;

      &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }

      &::-webkit-scrollbar-track {
        border-radius: 100px;
        background: var(--layer-floor-2);
      }

      &::-webkit-scrollbar-thumb {
        border-radius: 100px;
        background-color: var(--bg-primary);
      }
    }

    @include tablet-768 {
      height: 100%;
      background-color: var(--layer-floor-1);
    }
  }

  &-missing {
    padding-left: $spacing-600;

    &__text {
      color: var(--content-secondary)
    }
  }

  &-collection {
    margin-bottom: $spacing-600;
  }
}

:deep(.header-search-content) {
  width: 100%;
  max-height: calc(100vh - 154px);
  //overflow-y: auto!important;

  @include tablet-768 {
    height: 100%;
    max-height: 100%;
    background-color: var(--layer-floor-1);
  }
}
</style>
